import React, { ReactNode } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider } from '../../features/auth/context/AuthContext';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { ChatDock } from '../../features/chat/components/ChatDock';
import { ChatProvider } from '../../features/chat/context/ChatProvider';
import { QueryProvider } from '../../shared/providers/QueryProvider';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders wraps the entire application with all necessary context providers.
 * This centralizes provider configuration and makes App.tsx cleaner.
 * 
 * Provider order:
 * 1. QueryProvider - React Query for server state management (outermost)
 * 2. Router - Provides routing context
 * 3. AuthProvider - Provides authentication state
 * 4. ChatProvider - Provides chat/messaging state (depends on AuthProvider)
 */
export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  return (
    <QueryProvider>
      <Router>
        <AuthProvider>
          <ChatProvider>
            {children}
            <AuthenticatedChatDock />
          </ChatProvider>
        </AuthProvider>
      </Router>
    </QueryProvider>
  );
};

const AuthenticatedChatDock: React.FC = () => {
  const { session } = useAuth();

  if (!session) {
    return null;
  }

  return <ChatDock />;
};
